/**
 * Soft!=Live Page207 — Settings Cameras residual liquid (Page111/58 + dual).
 * Mount: /settings/cameras
 * Buses (contract ops — never painted in operator DOM).
 * Soft residual deleted:
 *  - Soft invent seats while posture loading
 *  - Soft catalogue rows when catalogue bus sealed
 *  - Soft Bind / Delete when catalogue write sealed
 *  - Soft Probe / Health when probe bus sealed
 *  - path /operation labels painted in DOM
 * Marker: Page207 Soft residual
 */
import { useMemo, useState } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { Receipt } from "./layered-console.js";

/** Path-free operator copy for Cameras desk. */
export function softCameraSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient|admin required/.test(low)) {
    return "Your account cannot manage cameras here. Ask a tenant admin if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|host|owner_admin|scope/.test(low)) {
    return "That camera change was not accepted. Check the host and tenant scope fields.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Camera services are not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/admin\/cameras|\/settings\/cameras|GET |POST |DELETE |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Soft≠Live Page207: missing contract ops must not crash the desk. */
function safeCamerasPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_cameras_status") return "/settings/cameras/status";
    if (op === "get__admin_cameras") {
      const q = opts && opts.query && opts.query.owner_admin;
      return q
        ? "/admin/cameras?owner_admin=" + encodeURIComponent(q)
        : "/admin/cameras";
    }
    if (op === "post__admin_cameras") return "/admin/cameras";
    if (op === "post__admin_cameras_probe-test") return "/admin/cameras/probe-test";
    if (op === "post__admin_cameras_lan-scan") return "/admin/cameras/lan-scan";
    if (op === "delete__admin_cameras_p") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/admin/cameras/" + encodeURIComponent(id) : "";
    }
    if (op === "post__admin_cameras_p_health-probe") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id
        ? "/admin/cameras/" + encodeURIComponent(id) + "/health-probe"
        : "";
    }
    return "";
  }
}

/** Pure: seat chips from Live Settings Cameras posture (Page207). */
export function cameraSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const max = Number(lim.catalogue_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    catalogueAllowed: p.catalogue_bind_allowed === true,
    catalogueWriteAllowed: p.catalogue_write_allowed === true,
    probeAllowed: p.probe_health_allowed === true,
    saScopeRequired: p.sa_scope_required === true,
    reReadAllowed: p.re_read_allowed === true,
    productFlag: p.product_flag === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim(),
    cataloguePaintMax:
      Number.isFinite(max) && max > 0 ? Math.floor(max) : 80,
  };
}

/** Pure: KPI strip from Live list pack. */
export function cameraCatalogKpisOf(pack, opts) {
  const items = (pack && (pack.items || pack.cameras)) || (Array.isArray(pack) ? pack : []);
  const online = items.filter((c) => {
    const st = String(c.health_status || c.status || "").toLowerCase();
    return st.includes("online") || st === "ok";
  }).length;
  const o = opts && typeof opts === "object" ? opts : {};
  return {
    bound: items.length,
    online,
    tenant: pack && pack.tenant_id ? String(pack.tenant_id) : (o.tenant || "—"),
    snapFlag: !!o.snapFlag,
  };
}

function listPath(seat, isSa, ownerAdmin) {
  if (!seat.catalogueAllowed) return null;
  const oa = String(ownerAdmin || "").trim();
  if (isSa || seat.saScopeRequired) {
    if (!oa) return null;
    return safeCamerasPath("get__admin_cameras", { query: { owner_admin: oa } });
  }
  return safeCamerasPath("get__admin_cameras");
}

function writeQuery(isSa, ownerAdmin, seat) {
  const oa = String(ownerAdmin || "").trim();
  if ((isSa || seat.saScopeRequired) && oa) return { query: { owner_admin: oa } };
  return {};
}

export function CameraSettingsDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isSa = !!(me && me.role === "superadmin");
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";
  const posture = useApi(safeCamerasPath("get__settings_cameras_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats sealed until Live.
  const seat = cameraSettingsSeatOf(posture.loading || !posture.data ? {} : post);
  const snapFlag = seat.productFlag === true;
  const postureLabel = seat.postureLabel
    || (seatSealedFallback(seat, role));

  const canMutate = seat.catalogueWriteAllowed;
  const canProbe = seat.probeAllowed;

  const [ownerAdmin, setOwnerAdmin] = useState("");
  const [scopeApplied, setScopeApplied] = useState("");
  const list = useApi(listPath(seat, isSa, scopeApplied));
  const { run, busy, envelope } = useAction();
  const [form, setForm] = useState({
    label: "",
    ip: "",
    port: "80",
    username: "",
    password: "",
  });
  const [scanRows, setScanRows] = useState([]);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanNote, setScanNote] = useState("");
  const [receipt, setReceipt] = useState(null);
  const setF = (k, v) => setForm((x) => ({ ...x, [k]: v }));

  const items = useMemo(() => {
    const d = list.data;
    const rows = (d && (d.items || d.cameras)) || (Array.isArray(d) ? d : []);
    return rows.slice(0, seat.cataloguePaintMax);
  }, [list.data, seat.cataloguePaintMax]);

  const kpis = cameraCatalogKpisOf(list.data, {
    snapFlag,
    tenant: isSa ? scopeApplied : (me && (me.tenant || me.username)) || "—",
  });

  const denied = !!(list.error && /\b403\b|forbidden/i.test(list.error || ""));
  const needOwner = !!(list.error && /owner_admin/i.test(list.error || ""));
  const saPending = (isSa || seat.saScopeRequired) && seat.catalogueAllowed && !scopeApplied;
  const seatSealed = !seat.catalogueAllowed && !seat.probeAllowed;
  const formHost = () => {
    const ip = String(form.ip || "").trim();
    const port = String(form.port || "80").trim() || "80";
    if (!ip) return "";
    return port === "80" ? ip : `${ip}:${port}`;
  };

  const applyScope = () => {
    if (!seat.catalogueAllowed) {
      toast && toast("Camera catalogue is not available on this seat.", "warn");
      return;
    }
    const oa = String(ownerAdmin || "").trim();
    if (!oa) {
      toast && toast("Choose which organisation tenant to act for.", "warn");
      return;
    }
    setScopeApplied(oa);
    toast && toast("Tenant scope applied · " + oa, "go");
  };

  const reload = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available right now.", "warn");
      return;
    }
    posture.reload && posture.reload();
    list.reload && list.reload();
    toast && toast("Camera catalogue refreshed", "go");
  };

  const probeBody = (cam) => {
    const host = String((cam && cam.host) || formHost() || "").trim();
    return {
      label: (cam && (cam.label || cam.camera_id)) || form.label || "probe",
      protocol: "auto",
      brand: "generic",
      host,
      ip: String((cam && cam.ip) || form.ip || "").trim(),
      port: Number((cam && cam.port) || form.port || 80) || 80,
      username: (cam && cam.username) || form.username || "",
      password: (cam && cam.password) || form.password || "",
    };
  };

  const probe = async (cam) => {
    if (!canProbe) {
      toast("Probe is not available on this seat.", "warn");
      return;
    }
    const body = probeBody(cam || form);
    if (!body.host && !body.ip) {
      toast("IP address is required", "bad");
      return;
    }
    const out = await run(safeCamerasPath("post__admin_cameras_probe-test"), {
      method: "POST",
      body,
    });
    if (out && out.ok !== false) {
      const hint = out.brand_hint || out.brand || "";
      toast(hint ? (`Probe ok · ${hint}`) : "Probe reached the camera", "go");
    } else {
      toast(softCameraSettingsErrorOf((out && (out.health_status || out.tag || out.error)) || "Probe failed"), "bad");
    }
  };

  const create = async () => {
    if (!canMutate || !seat.catalogueAllowed) {
      toast("Binding cameras is not available on this seat.", "warn");
      return;
    }
    if ((isSa || seat.saScopeRequired) && !scopeApplied) {
      toast("Apply a tenant scope before binding.", "warn");
      return;
    }
    if (!String(form.ip || "").trim() && !formHost()) {
      toast("IP address is required", "bad");
      return;
    }
    const q = writeQuery(isSa, scopeApplied, seat);
    const out = await run(safeCamerasPath("post__admin_cameras", q), {
      method: "POST",
      body: {
        label: String(form.label || "").trim() || String(form.ip || "").trim(),
        protocol: "auto",
        brand: "generic",
        ip: String(form.ip || "").trim(),
        port: Number(form.port || 80) || 80,
        host: formHost(),
        username: form.username || "",
        password: form.password || "",
      },
    });
    if (!out) {
      toast(softCameraSettingsErrorOf("Create refused") || "Create refused", "bad");
      return;
    }
    setForm({ label: "", ip: "", port: "80", username: "", password: "" });
    list.reload && list.reload();
    const brand = out.brand_hint || out.brand || "";
    const camId = String(out.camera_id || out.id || "").trim();
    setReceipt({
      title: "Camera registered",
      accepted: camId || String(form.ip || "").trim() || "accepted",
      effective: scopeApplied ? ("Scope · " + scopeApplied) : "Tenant catalogue",
      evidence: (brand ? brand + " · " : "") + "Live POST catalogue",
      at: new Date().toISOString(),
      next: "Open Register & bind on a board to attach this lens.",
      tone: "go",
    });
    toast(
      "Camera registered · " + (camId || form.ip) + (brand ? (" · " + brand) : ""),
      "go",
    );
  };

  const fillFromScan = (row) => {
    setForm((x) => ({
      ...x,
      ip: String(row.ip || "").trim(),
      port: String(row.port || 80),
      label: x.label || String(row.brand || row.brand_hint || row.ip || ""),
    }));
    setScanNote("Filled from LAN scan · " + (row.brand || row.ip));
    toast("Filled from scan · " + (row.brand || row.ip), "go");
  };

  const runLanScan = async () => {
    if (!canProbe && !canMutate) {
      toast("Scan is not available on this seat.", "warn");
      return;
    }
    setScanBusy(true);
    setScanNote("Scanning LAN…");
    const out = await run(safeCamerasPath("post__admin_cameras_lan-scan"), {
      method: "POST",
      body: { cidr: "192.168.1.0/24", limit_hosts: 256 },
    });
    setScanBusy(false);
    if (!out || out.ok === false) {
      setScanRows([]);
      setScanNote(softCameraSettingsErrorOf((out && out.detail) || "LAN scan unavailable — use P4 edge search under Fleet → device → Cameras"));
      toast("LAN scan unavailable on this host", "warn");
      return;
    }
    const rows = Array.isArray(out.items) ? out.items : [];
    setScanRows(rows);
    setScanNote("Found " + rows.length + " candidate(s)");
    toast("LAN scan · " + rows.length + " camera candidate(s)", "go");
  };

  const remove = async (cam) => {
    if (!canMutate || !seat.catalogueAllowed) {
      toast("Deleting cameras is not available on this seat.", "warn");
      return;
    }
    if ((isSa || seat.saScopeRequired) && !scopeApplied) {
      toast("Apply a tenant scope before deleting.", "warn");
      return;
    }
    const id = cam.camera_id || cam.id;
    if (!id) return;
    if (!window.confirm("Delete camera " + id + "? Stored credentials are wiped.")) return;
    const q = writeQuery(isSa, scopeApplied, seat);
    const out = await run(
      safeCamerasPath("delete__admin_cameras_p", { pathParams: [id], ...q }),
      { method: "DELETE" },
    );
    if (!out) {
      toast(softCameraSettingsErrorOf("Delete refused") || "Delete refused", "bad");
      return;
    }
    list.reload && list.reload();
    toast("Camera deleted · " + id, "warn");
  };

  const healthProbe = async (cam) => {
    if (!canProbe) {
      toast("Health check is not available on this seat.", "warn");
      return;
    }
    const id = cam.camera_id || cam.id;
    if (!id) return;
    const out = await run(
      safeCamerasPath("post__admin_cameras_p_health-probe", { pathParams: [id] }),
      { method: "POST", body: {} },
    );
    if (!out) {
      toast(softCameraSettingsErrorOf("Health check refused") || "Health check refused", "bad");
      return;
    }
    list.reload && list.reload();
    const st = out.health_status || out.status || "probed";
    toast("Health · " + id + " · " + st, /online|ok/i.test(String(st)) ? "go" : "warn");
  };

  if (seatSealed) {
    return html`<div className="cam-set-desk cam-set-desk--liquid"
      data-cmc-page="page111-cameras" data-cmc-cameras="page58"
      data-cmc-residual="page207-cameras" data-cmc-cameras111="page111-cameras">
      <div className="cam-set-desk__rail">
        <div>
          <p className="eyebrow">Settings · Cameras</p>
          <h2 className="cam-set-desk__title">Camera seats sealed</h2>
          <p className="note">${postureLabel}. Catalogue and health probe are separate seats.</p>
        </div>
        <div className="btn-row cam-set-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${busy || !seat.reReadAllowed} onClick=${reload}>
            <${Ico} d="refresh" w=${14}/> Refresh
          </button>
        </div>
      </div>
      ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}
      <${Alert} tone="info" title="Camera catalogue and probe seats"
        say=${`catalogue=${seat.catalogueAllowed ? "open" : "sealed"} · probe=${seat.probeAllowed ? "open" : "sealed"} · ${seat.role || (isSaStaff ? "staff" : isUser ? "user" : role) || "—"}`}/>
      <${Alert} tone="warn" title="Cannot manage cameras on this session"
        say="User and staff seats cannot bind, delete, or probe cameras. Ask a tenant admin."/>
      <${Card} title="Cameras posture" meta="status"
          sub="Always-on honesty bus — catalogue only from Live.">
        ${posture.error ? html`<${Alert} tone="warn" title="Could not load posture" say=${softCameraSettingsErrorOf(posture.error)}/>` : null}
        <div className="cam-set-desk__kpis">
          <div className="cam-set-desk__kpi" data-on="0"><span>Catalogue</span><b>sealed</b><em>bind</em></div>
          <div className="cam-set-desk__kpi" data-on="0"><span>Probe</span><b>sealed</b><em>health</em></div>
          <div className="cam-set-desk__kpi" data-on="0"><span>Your seat</span><b>${isSaStaff ? "staff" : isUser ? "user" : (role || "—")}</b><em>Server seat</em></div>
          <div className="cam-set-desk__kpi" data-on=${seat.productFlag ? "1" : "0"}>
            <span>Snapshot flag</span><b>${seat.productFlag ? "on" : "off"}</b><em>honesty</em>
          </div>
        </div>
      <//>
    </div>`;
  }

  return html`<div className="cam-set-desk cam-set-desk--liquid"
    data-cmc-page="page111-cameras" data-cmc-cameras="page58"
    data-cmc-residual="page207-cameras" data-cmc-cameras111="page111-cameras"
    data-cmc-cam-receipt=${receipt ? "1" : "0"}>
    ${receipt ? html`<${Receipt} title=${receipt.title} accepted=${receipt.accepted}
      effective=${receipt.effective} evidence=${receipt.evidence} at=${receipt.at}
      next=${receipt.next} tone=${receipt.tone || "go"}/>` : null}
    <div className="cam-set-desk__rail">
      <div>
        <p className="eyebrow">Settings · Cameras</p>
        <h2 className="cam-set-desk__title">Tenant camera catalogue</h2>
        <p className="note">
          ${postureLabel}.
          Catalogue is the lens list. Bind a board on this same page — not a second registry.
        </p>
      </div>
      <div className="btn-row cam-set-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${busy || saPending || !seat.reReadAllowed}
          onClick=${reload} title="Refresh catalogue">
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
      </div>
    </div>

    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <${Alert} tone="info" title="Camera catalogue and probe seats"
      say=${`catalogue=${seat.catalogueAllowed ? "open" : "sealed"} · probe=${seat.probeAllowed ? "open" : "sealed"} · ${seat.role || role || "—"}`}/>

    <div className="cam-set-desk__kpis" role="group" aria-label="Camera KPIs">
      <div className="cam-set-desk__kpi" data-on=${kpis.bound && seat.catalogueAllowed ? "1" : "0"}>
        <span>Bound</span>
        <b>${saPending || denied || !seat.catalogueAllowed ? "—" : kpis.bound}</b>
        <em>catalogue</em>
      </div>
      <div className="cam-set-desk__kpi" data-on=${kpis.online && seat.catalogueAllowed ? "1" : "0"}>
        <span>Online</span>
        <b>${saPending || denied || !seat.catalogueAllowed ? "—" : kpis.online}</b>
        <em>health tip</em>
      </div>
      <div className="cam-set-desk__kpi" data-on=${seat.probeAllowed ? "1" : "0"}>
        <span>Probe bus</span>
        <b>${seat.probeAllowed ? "open" : "sealed"}</b>
        <em>independent</em>
      </div>
      <div className="cam-set-desk__kpi" data-on=${snapFlag ? "1" : "0"}>
        <span>Snapshot flag</span>
        <b>${snapFlag ? "on" : "off"}</b>
        <em>delivery honesty</em>
      </div>
    </div>

    ${!snapFlag ? html`<${Alert} tone="info" title="Snapshot delivery flag is off"
      say="Edge capture delivery may stay unarmed on this deployment. The catalogue list and bind still follow the server seat — they are not invented locally."/>` : null}

    ${(isSa || seat.saScopeRequired) ? html`
      <${Card} title="Platform tenant scope" meta="scope"
          sub="Platform catalogue writes need an organisation tenant. Writes need an organisation scope.">
        <div className="grid g2">
          <${Field} label="Organisation tenant (admin username)">
            <${Input} icon="user" value=${ownerAdmin}
              onChange=${(e)=>setOwnerAdmin(e.target.value)}
              placeholder="tenant admin username" autocomplete="off"/>
          <//>
        </div>
        <div className="btn-row" style=${{ marginTop: 10 }}>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !String(ownerAdmin || "").trim() || !seat.catalogueAllowed}
            onClick=${applyScope}>Apply scope</button>
          ${scopeApplied ? html`<span className="note">Active · ${scopeApplied}</span>` : null}
        </div>
      <//>` : null}

    ${saPending ? html`
      <${Alert} tone="warn" title="Tenant scope required"
        say="Platform operators must apply a tenant admin username before the catalogue loads."/>` : null}

    ${denied ? html`
      <${Alert} tone="info" title="Admin access required"
        say=${softCameraSettingsErrorOf(list.error || "Admin required")}/>` : null}

    ${needOwner && !saPending ? html`
      <${Alert} tone="warn" title="Tenant scope required"
        say=${softCameraSettingsErrorOf(list.error || "Set platform tenant scope and refresh.")}/>` : null}

    ${!seat.catalogueAllowed ? html`
      <${Alert} tone="warn" title="Catalogue sealed"
        say="Cannot invent list, bind, or delete without a catalogue seat."/>` : null}

    ${seat.catalogueAllowed && !saPending && !denied ? html`<${F}>
      <${Card} title="Bound cameras" meta=${items.length ? items.length + " cameras" : "catalogue"}
          sub="Credentials are write-only — never echoed in the list.">
        ${list.loading
          ? html`<p className="note">Loading catalogue…</p>`
          : list.error && !needOwner
            ? html`<${Alert} tone="warn" title="Could not load cameras"
                say=${softCameraSettingsErrorOf(list.error || "The server did not answer.")}/>`
            : html`<div className="rows">
                ${!items.length ? html`<p className="note">No cameras bound yet — use Bind below.</p>` : null}
                ${items.map((c) => {
                  const id = c.camera_id || c.id;
                  const st = String(c.health_status || c.status || "").toLowerCase();
                  const tone = st.includes("online") || st === "ok" ? "on"
                    : st.includes("slow") || st.includes("warn") ? "alarm" : "crit";
                  return html`
                    <div key=${id} className="row" data-state=${tone}>
                      <span className="st"/>
                      <span className="main">
                        <b>${c.label || id}</b>
                        <span>${c.host || "—"} · ${c.protocol || "http_snapshot"}</span>
                      </span>
                      <span className="trail">${st || "unknown"}</span>
                      <button type="button" className="btn btn--ghost btn--xs"
                        disabled=${busy || !canProbe}
                        onClick=${()=>healthProbe(c)}
                        title="Check health with stored credentials">Health</button>
                      ${c.host ? html`<button type="button" className="btn btn--ghost btn--xs"
                        disabled=${busy || !canProbe}
                        onClick=${()=>probe({ ...c, username: "", password: "" })}
                        title="Ephemeral probe — list cannot re-send secrets">Probe</button>` : null}
                      <button type="button" className="btn btn--danger btn--xs"
                        disabled=${busy || !canMutate}
                        onClick=${()=>remove(c)}>Delete</button>
                    </div>`;
                })}
              </div>`}
      <//>

      ${canMutate ? html`
        <${Card} title="Register a camera" meta="unified" cls="mt"
            sub="IP · port · user · password — brand is detected, not chosen.">
          <div className="grid g2">
            <${Field} label="Label"><${Input} icon="camera" value=${form.label}
              onChange=${(e)=>setF("label", e.target.value)} placeholder="Gate east"/><//>
            <${Field} label="IP address (required)"><${Input} icon="map" value=${form.ip}
              onChange=${(e)=>setF("ip", e.target.value)} placeholder="192.168.1.72" autocomplete="off"/><//>
            <${Field} label="Port"><${Input} icon="map" value=${form.port}
              onChange=${(e)=>setF("port", e.target.value)} placeholder="80" autocomplete="off"/><//>
            <${Field} label="Username"><${Input} icon="user" value=${form.username}
              onChange=${(e)=>setF("username", e.target.value)} autocomplete="off"/><//>
            <${Field} label="Password"><${Input} icon="lock" type="password" value=${form.password}
              onChange=${(e)=>setF("password", e.target.value)} autocomplete="new-password"/><//>
          </div>
          <div className="btn-row" style=${{ marginTop: 12 }}>
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${busy || scanBusy || (!canProbe && !canMutate)}
              onClick=${runLanScan}>${scanBusy ? "Scanning…" : "Scan LAN"}</button>
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${busy || !canProbe || (!String(form.ip || "").trim() && !formHost())}
              onClick=${()=>probe(form)}>Probe before save</button>
            <button type="button" className="btn btn--go btn--sm"
              disabled=${busy || !canMutate || (!String(form.ip || "").trim() && !formHost()) || ((isSa || seat.saScopeRequired) && !scopeApplied)}
              onClick=${create}>${busy ? "Writing…" : "Register camera"}</button>
          </div>
          ${scanNote ? html`<p className="note" style=${{ marginTop: 8 }}>${scanNote}</p>` : null}
          ${scanRows.length ? html`<div className="rows" style=${{ marginTop: 10 }} role="list">
            ${scanRows.map((r) => html`
              <div key=${r.ip + ":" + r.port} className="row" role="listitem">
                <span className="main">
                  <b>${r.ip}</b>
                  <span>port ${r.port}${(r.ports || []).length > 1 ? (" · " + (r.ports || []).join(", ")) : ""} · ${r.brand || r.brand_hint || "Unknown"}${r.rtsp ? " · RTSP" : ""}</span>
                </span>
                <button type="button" className="btn btn--ghost btn--xs"
                  disabled=${busy}
                  onClick=${()=>fillFromScan(r)}>Use</button>
              </div>`)}
          </div>` : null}
          <p className="note">P4 edge search (ESP32-P4 on COM) lives under Fleet → device → Cameras. Server LAN scan needs CAMERA_LAN_SCAN_ENABLED on a LAN-colocated API.</p>
        <//>` : html`
        <${Alert} tone="info" title="Catalogue write sealed"
          say="Bind and delete need a catalogue write seat."/>`}
    <//>` : null}
  </div>`;
}

function seatSealedFallback(seat, role) {
  if (!seat.catalogueAllowed && !seat.probeAllowed) {
    if (role === "user") return "Camera catalogue sealed for this account";
    if (role === "sa_staff") return "Camera catalogue sealed for staff (tenant admin seat required)";
    return "Camera seats sealed";
  }
  return "Camera catalogue";
}

export default CameraSettingsDesk;
